import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { IInboxJobFilter } from '../interfaces/inbox-job-filter';
import { flatten } from '../utils/flatten';
import { SupplierInboxJobsService } from './supplier-inbox-jobs.service';
import { ISupplierInboxJob } from '../interfaces/inbox-job';
import { SupplierJobInteractionService } from './supplier-job-interaction.service';
import { Router } from '@angular/router';
import { SupplierInboxType } from '../enums/supplier-inbox.type.enum';

@Injectable({
  providedIn: 'root',
})
export class SupplierJobStoreService {
  private readonly _supplierJobs: BehaviorSubject<ISupplierInboxJob[]> = new BehaviorSubject<
    ISupplierInboxJob[]
  >([]);

  private readonly _loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private readonly _selectedJob: BehaviorSubject<ISupplierInboxJob> =
    new BehaviorSubject<ISupplierInboxJob>(null);

  private jobTotal: number = 0;

  public isGridView: boolean = false;

  private _acceptJobDialogVisible: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private _hideJobDialogVisible: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private _editScheduleDialogVisible: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false,
  );

  private _allocateDriverDialogVisible: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false,
  );

  private _allocateDepotDialogVisible: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false,
  );

  private _allocateTruckDialogVisible: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false,
  );

  public selectedTab: SupplierInboxType;

  constructor(
    private supplierInboxJobsService: SupplierInboxJobsService,
    private supplierJobInteractionService: SupplierJobInteractionService,
    private router: Router,
  ) {}

  get jobs() {
    return this._supplierJobs.asObservable();
  }

  get totalRecords() {
    return this.jobTotal;
  }

  get loading$() {
    return this._loading.asObservable();
  }

  get selectedJob$() {
    return this._selectedJob.asObservable();
  }

  get acceptJobDialogVisible$() {
    return this._acceptJobDialogVisible.asObservable();
  }

  get allocateDriverDialogVisible$() {
    return this._allocateDriverDialogVisible.asObservable();
  }

  get allocateDepotDialogVisible$() {
    return this._allocateDepotDialogVisible.asObservable();
  }

  get allocateTruckDialogVisible$() {
    return this._allocateTruckDialogVisible.asObservable();
  }

  get hideJobDialogVisible$() {
    return this._hideJobDialogVisible.asObservable();
  }

  get editScheduleDialogVisible$() {
    return this._editScheduleDialogVisible.asObservable();
  }

  setEditScheduleDialogVisible(visible: boolean) {
    this._editScheduleDialogVisible.next(visible);
  }

  setAcceptJobDialogVisible(visible: boolean) {
    this._acceptJobDialogVisible.next(visible);
  }

  setAllocateDriverDialogVisible$(visible: boolean) {
    this._allocateDriverDialogVisible.next(visible);
  }

  setHideJobDialogVisible(visible: boolean) {
    this._hideJobDialogVisible.next(visible);
  }

  setAllocateDepotDialogVisible$(visible: boolean) {
    this._allocateDepotDialogVisible.next(visible);
  }

  setAllocateTruckDialogVisible$(visible: boolean) {
    this._allocateTruckDialogVisible.next(visible);
  }

  removedCount = 0;

  selectJob(job: ISupplierInboxJob) {
    this._selectedJob.next(job);
  }

  removeJob(jobId: number) {
    const jobsArr = this._supplierJobs.getValue();
    for (let i = 0; i < jobsArr.length; i++) {
      if (jobsArr[i].id === jobId) {
        this.removedCount++;
        jobsArr.splice(i, 1);
        this._supplierJobs.next(jobsArr);
        return;
      }
    }
  }

  setJobWatching(jobId: number, watching: boolean) {
    const jobsArr = this._supplierJobs.getValue();
    let job = jobsArr.find((x) => x.id === jobId);
    if (job) {
      if (job.supplierWatching !== watching) {
        job.supplierWatchCount = (job.supplierWatchCount ?? 0) + (watching ? 1 : -1);
      }
      job.supplierWatching = watching;
    }
  }

  setJobHidden(jobId: number, hidden: boolean) {
    const jobsArr = this._supplierJobs.getValue();
    let job = jobsArr.find((x) => x.id === jobId);
    if (job) {
      job.supplierRejected = hidden;
    }
  }

  watchJob(job: ISupplierInboxJob) {
    this.supplierJobInteractionService.logSingleJobInteraction('WatchJobAttempt', job.id);
    this.supplierInboxJobsService.watchJob(job.id).then(() => {
      this.supplierJobInteractionService.logSingleJobInteraction('WatchJobSuccess', job.id);
      this.setJobWatching(job.id, true);
    });
  }

  unWatchJob(jobId: number) {
    this.supplierInboxJobsService.unWatchJob(jobId).then(() => {
      if (this.selectedTab == SupplierInboxType.InboxBidWatch) {
        this.removeJob(jobId);
      }
      this.setJobWatching(jobId, false);
      this.supplierJobInteractionService.logSingleJobInteraction('UnwatchJobSuccess', jobId);
    });
  }

  unHideJob(jobId: number) {
    return this.supplierInboxJobsService.unRejectJob(jobId);
  }

  goToJobDetails(jobId: number, event: MouseEvent) {
    this.supplierJobInteractionService.logSingleJobInteraction('JobDetailsView', jobId);

    const url = 'app/jobscreen/' + jobId;

    if (event?.ctrlKey || event?.metaKey) {
      // ctrl was held during click
      window.open(url, '_blank');
    } else {
      void this.router.navigate([url]);
    }
  }

  goToRouteDetails(routeId: number, event: MouseEvent) {
    // this.supplierJobInteractionService.logSingleJobInteraction('RouteDetailsView', jobId);

    const url = 'app/planned-route-detail/' + routeId;

    if (event?.ctrlKey || event?.metaKey) {
      // ctrl was held during click
      window.open(url, '_blank');
    } else {
      void this.router.navigate([url]);
    }
  }

  goToPriceDetails(jobId: number, event: MouseEvent) {
    const url = 'app/pricedetails?jobId=' + jobId;

    if (event?.ctrlKey || event?.metaKey) {
      // ctrl was held during click
      window.open(url, '_blank');
    } else {
      void this.router.navigate([url]);
    }
  }

  clearJobs() {
    this._supplierJobs.next(null);
  }

  getLastJob() {
    const jobs = this._supplierJobs.getValue();
    return jobs[jobs.length - 1];
  }

  getNextPageIfRequired(filter: IInboxJobFilter, visibleJobId: number) {
    const lastJob = this.getLastJob();

    if (visibleJobId != lastJob.id) {
      return;
    }

    this.getPage(filter);
  }

  private currentRequestNumber = 0;

  getPage(filter: IInboxJobFilter, fromStart = false) {
    const requestNumber = ++this.currentRequestNumber;

    this._loading.next(true);

    const filterVal = Object.assign({}, filter);

    let currentValue = this._supplierJobs.getValue();

    if (!this.isGridView) {
      filterVal.first = fromStart ? 0 : currentValue.length;
    }

    if (filterVal.first == 0 && !this.isGridView) {
      currentValue = [];
      this.clearJobs();
    }

    // filterVal.first = Math.max(0, filterVal.first - this.removedCount);

    this.supplierInboxJobsService
      .getThePage(flatten(filterVal))
      .then((result) => {
        // if this is not the latest request, ignore it.
        // This is to fix the bug where switching between My Jobs and Jobs Search pages would load stale data.
        if (requestNumber !== this.currentRequestNumber) {
          return;
        }

        this.jobTotal = result.total;

        let re: ISupplierInboxJob[] = result.items.map((job) => {
          job.tasks.map((task) => {
            if (!task.route) {
              task.route = {
                distance: task.distance,
                polyline: task.polyline,
              };
            }

            // task.route.imageUrl = this.mapService.getStaticMapPath(
            //   'roadmap',
            //   this.mapService.buildMarkers(job, task.id),
            //   this.staticMapSize,
            //   1,
            //   this.mapService.buildPathsAsPolyline(job, task.id),
            // );
          });

          return job;
        });

        !!currentValue && !this.isGridView
          ? this._supplierJobs.next([...currentValue, ...re])
          : this._supplierJobs.next([...re]);

        this.unselectJobIfRequired();

        if (this._selectedJob.getValue() == null && re.length > 0) {
          this._selectedJob.next(re[0]);
        }

        if (result.total == 0) {
          this.clearJobs();
        }
      })
      .finally(() => {
        // Only stop loading if this is the latest request
        if (requestNumber === this.currentRequestNumber) {
          this._loading.next(false);
        }
      });
  }

  unselectJobIfRequired() {
    const selectedJob = this._selectedJob.getValue();

    if (!selectedJob) {
      return;
    }

    if (this._supplierJobs.getValue().find((x) => x.id === selectedJob.id)) {
      return;
    }

    this._selectedJob.next(null);
  }

  isMyJobs(inboxType: SupplierInboxType) {
    const myJobsTypes = [
      SupplierInboxType.ExistingUnallocated,
      SupplierInboxType.ExistingWaiting,
      SupplierInboxType.ExistingCompleted,
      SupplierInboxType.ExistingLive,
      SupplierInboxType.ExistingCancelled,
      SupplierInboxType.ExistingClosed,
    ];

    return myJobsTypes.includes(inboxType);
  }

  handBackEnabled(inboxType: SupplierInboxType) {
    const handBackEnabledTypes = [
      SupplierInboxType.ExistingUnallocated,
      SupplierInboxType.ExistingWaiting,
    ];

    return handBackEnabledTypes.includes(inboxType);
  }
}
